import React, { useState, useEffect } from "react"
import config from "../config"

const ContactForm = () => {
  // Form state
  const [formData, setFormData] = useState({
    fullName: "",
    companyName: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    captchaAnswer: "",
    honeypot: "" // This is a honeypot field to catch bots
  });
  
  // Math captcha state
  const [mathProblem, setMathProblem] = useState({ num1: 0, num2: 0, answer: 0 });
  
  // Form submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [formToken, setFormToken] = useState("");

  // Generate a new math problem
  const generateMathProblem = () => {
    const num1 = Math.floor(Math.random() * 40) + 10; // num1 from 10 to 49
    // Ensure num2 is at least 10 and num1 + num2 <= 99
    // So, max value for num2 is 99 - num1
    const maxNum2 = 99 - num1;
    // num2 from 10 up to (99 - num1)
    const num2 = Math.floor(Math.random() * (maxNum2 - 10 + 1)) + 10; 
    setMathProblem({
      num1,
      num2,
      answer: num1 + num2
    });
  };

  // Generate a token for the form
  const generateToken = () => {
    const timestamp = Date.now();
    const randomStr = Math.random().toString(36).substring(2, 15);
    setFormToken(`${timestamp}_${randomStr}`);
  };

  // Initialize captcha and token on component mount
  useEffect(() => {
    generateMathProblem();
    generateToken();
  }, []);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Validate form
  const validateForm = () => {
    console.log("=== FORM VALIDATION DEBUG ===");
    console.log("Form Data:", formData);
    console.log("Math Problem:", mathProblem);
    
    // Check required fields with proper trimming
    if (!formData.fullName || !formData.fullName.trim()) {
      console.log("FAILED: Full Name validation");
      return "Full Name is required";
    }
    if (!formData.email || !formData.email.trim()) {
      console.log("FAILED: Email validation");
      return "Email Address is required";
    }
    if (!formData.subject || !formData.subject.trim()) {
      console.log("FAILED: Subject validation");
      return "Subject is required";
    }
    if (!formData.message || !formData.message.trim()) {
      console.log("FAILED: Message validation");
      return "Message is required";
    }
    
    // Basic email validation with trimming
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const trimmedEmail = formData.email.trim();
    if (!emailRegex.test(trimmedEmail)) {
      console.log("FAILED: Email format validation");
      return "Please enter a valid email address";
    }
    
    // Captcha validation with proper number handling
    const captchaValue = formData.captchaAnswer.trim();
    console.log("Captcha validation:", {
      captchaValue,
      parsedValue: parseInt(captchaValue),
      expectedAnswer: mathProblem.answer,
      isValid: parseInt(captchaValue) === mathProblem.answer
    });
    
    if (!captchaValue || isNaN(captchaValue) || parseInt(captchaValue) !== mathProblem.answer) {
      console.log("FAILED: Captcha validation");
      return "Incorrect captcha answer";
    }
    
    // Honeypot check (should be empty)
    if (formData.honeypot && formData.honeypot.trim()) {
      console.log("FAILED: Honeypot validation");
      return "Form submission failed";
    }
    
    console.log("VALIDATION PASSED - All checks successful");
    return "";
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Reset error state
    setSubmitError("");
    
    // Validate the form
    const error = validateForm();
    if (error) {
      setSubmitError(error);
      return;
    }
    
    // Start submission
    setIsSubmitting(true);
    
    try {
      // Prepare data for submission with correct field names for backend
      const submissionData = {
        fullName: formData.fullName,
        companyName: formData.companyName,
        email: formData.email,
        phone: formData.phone,
        subject: formData.subject,
        message: formData.message,
        captcha: formData.captchaAnswer, // Backend expects 'captcha', not 'captchaAnswer'
        captchaToken: formToken, // Backend expects 'captchaToken', not 'token'
        timestamp: Date.now(),
      };
      
      // Honeypot field is already excluded by not including it in submissionData
      
      // Log submission data for debugging
      console.log("=== FORM SUBMISSION DEBUG ===");
      console.log("Submission data being sent:", submissionData);
      console.log("API endpoint:", config.API.CONTACT_FORM);
      
      // API endpoint pointing to the Lambda function
      const response = await fetch(config.API.CONTACT_FORM, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(submissionData),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Form submission failed");
      }
      
      // Handle success
      setSubmitSuccess(true);
      
      // Reset form
      setFormData({
        fullName: "",
        companyName: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
        captchaAnswer: "",
        honeypot: ""
      });
      
      // Generate a new math problem and token
      generateMathProblem();
      generateToken();
      
    } catch (error) {
      console.error("Form submission error:", error);
      setSubmitError(error.message || "An error occurred while submitting the form");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render the form
  return (
    <div className="uk-container">
      {submitSuccess ? (
        <div className="uk-alert uk-alert-success">
          <p>Thank you for your submission. <a href="https://developers.radioplayer.org">Radioplayer</a> will get back to you at the earliest opportunity.</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="uk-form-stacked">
          {/* Error message for non-captcha errors */}
          {submitError && submitError !== "Incorrect captcha answer" && (
            <div className="uk-alert uk-alert-danger">
              <p>{submitError}</p>
            </div>
          )}
          
          {/* Full Name */}
          <div className="uk-margin">
            <label className="uk-form-label" htmlFor="fullName">
              Full Name <span className="uk-text-danger">*</span>
            </label>
            <div className="uk-form-controls">
              <input
                id="fullName"
                name="fullName"
                className="uk-input"
                type="text"
                value={formData.fullName}
                onChange={handleChange}
                required
              />
            </div>
          </div>
          
          {/* Company Name */}
          <div className="uk-margin">
            <label className="uk-form-label" htmlFor="companyName">
              Company Name
            </label>
            <div className="uk-form-controls">
              <input
                id="companyName"
                name="companyName"
                className="uk-input"
                type="text"
                value={formData.companyName}
                onChange={handleChange}
              />
            </div>
          </div>
          
          {/* Email */}
          <div className="uk-margin">
            <label className="uk-form-label" htmlFor="email">
              Email Address <span className="uk-text-danger">*</span>
            </label>
            <div className="uk-form-controls">
              <input
                id="email"
                name="email"
                className="uk-input"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
          </div>
          
          {/* Phone */}
          <div className="uk-margin">
            <label className="uk-form-label" htmlFor="phone">
              Phone Number
            </label>
            <div className="uk-form-controls">
              <input
                id="phone"
                name="phone"
                className="uk-input"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
              />
            </div>
          </div>
          
          {/* Subject */}
          <div className="uk-margin">
            <label className="uk-form-label" htmlFor="subject">
              Subject <span className="uk-text-danger">*</span>
            </label>
            <div className="uk-form-controls">
              <input
                id="subject"
                name="subject"
                className="uk-input"
                type="text"
                value={formData.subject}
                onChange={handleChange}
                required
              />
            </div>
          </div>
          
          {/* Message */}
          <div className="uk-margin">
            <label className="uk-form-label" htmlFor="message">
              Message <span className="uk-text-danger">*</span>
            </label>
            <div className="uk-form-controls">
              <textarea
                id="message"
                name="message"
                className="uk-textarea"
                rows="5"
                value={formData.message}
                onChange={handleChange}
                required
              ></textarea>
            </div>
          </div>
          
          {/* Math Captcha */}
          <div className="uk-margin">
            <label className="uk-form-label" htmlFor="captchaAnswer">
              Security Check: What is {mathProblem.num1} + {mathProblem.num2}? <span className="uk-text-danger">*</span>
            </label>
            <div className="uk-form-controls">
              <input
                id="captchaAnswer"
                name="captchaAnswer"
                className="uk-input"
                type="text"
                inputMode="numeric"
                value={formData.captchaAnswer}
                onChange={handleChange}
                required
              />
            </div>
          </div>
          
          {/* Honeypot field - hidden from real users */}
          <div className="uk-hidden">
            <label htmlFor="honeypot">Leave this field empty</label>
            <input
              id="honeypot"
              name="honeypot"
              type="text"
              value={formData.honeypot}
              onChange={handleChange}
              tabIndex="-1"
              autoComplete="off"
            />
          </div>
          
          {/* Hidden token field */}
          <input type="hidden" name="token" value={formToken} />
          
          {/* Submit Button & Captcha Error */}
          <div className="uk-margin">
            {submitError && submitError === "Incorrect captcha answer" && (
              <div className="uk-alert uk-alert-danger uk-margin-small-bottom">
                <p>{submitError}</p>
              </div>
            )}
            <button 
              className="uk-button uk-button-primary" 
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Sending..." : "Send Message"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default ContactForm;
